import type { CSSProperties } from 'react';
import {
  CardLink,
  Icon,
  MediaHeader,
  Pill,
  Stack,
  TONE,
  type Tone,
} from '@leanin/ui';
import { Layers } from 'lucide-react';
import { useTranslations } from '@/i18n/translations';

import { toneFromId } from '@/lib/group-tone';
import { RouterLinkAs } from '@/lib/router-link';
import styles from './LearningTrackCard.module.css';
import { trackCoverUrl } from './track-cover';
import type { LearningTrackSummary } from '@/lib/queries/learning-tracks';

// Track tone cycle — no yellow (it reads muddy under the scrim); a track
// keeps its tone across visits. Exported so the detail hero tints with the
// SAME tone the card's cover fallback uses.
export const TRACK_TONES: ReadonlyArray<Tone> = [
  'burgundy',
  'navy',
  'emerald',
  'orange',
];

export const TRACK_CARD_WIDTH = 340;
export const TRACK_COVER_HEIGHT = 190;
// The room `.trackPeek` reserves right of and below the card for the peek
// layers. Owned here and handed to the CSS as `--peek-offset`, so the grid
// minimum below and the peek padding read one value.
export const TRACK_PEEK_OFFSET = 'var(--space-lg)';
// A grid cell holds the card plus its peek offset, so cards in the grid are
// never narrower than a rail card.
export const TRACK_GRID_MIN_WIDTH = `calc(${TRACK_CARD_WIDTH}px + ${TRACK_PEEK_OFFSET})` as const;

// Coverless fallback: a rich ink→accent→tint sweep instead of the pale
// tonal gradient, which washed to beige under the scrim.
function trackFallbackBg(tone: Tone): string {
  const t = TONE[tone];
  return `linear-gradient(160deg, ${t.ink} 0%, ${t.accent} 65%, ${t.bg} 130%)`;
}

// The stack-of-layers glyph is the tracks-only "playlist" mark (badge +
// resources chip). Not in the curated `I` map (single-feature use), so a
// feature-local lucide import per the curated-map rule.
const layersIcon = <Layers size="100%" strokeWidth={1.75} />;

// One learning track as a linked single-cover overlay card with the
// stacked-deck "peek" behind it. Shared by the Home rail and the Learning
// tracks tab's grid so the treatment can't drift between them. Leads with
// the same photo as the track's curriculum hero (`trackCoverUrl`); falls
// back to a tone-tinted sweep when neither the track nor any item has one.
//   rail: fixed width, so a CardRail scrolls instead of squeezing cards.
//   fill: takes its grid cell's width; pair with a CardGrid whose min is
//         TRACK_GRID_MIN_WIDTH so cards never get narrower than a rail card.
// `showBadge` is off where the surface already says these are tracks (the
// Learning tracks tab), so the chip doesn't repeat on every card.
export function LearningTrackCard({
  track,
  sizing,
  showBadge,
}: {
  readonly track: LearningTrackSummary;
  readonly sizing: 'rail' | 'fill';
  readonly showBadge: boolean;
}) {
  const t = useTranslations('LearningTracks');
  const cover = trackCoverUrl(track.coverImageUrl, track.previewCovers);
  const tone = toneFromId(track.id, TRACK_TONES);
  return (
    <div
      className={styles.trackPeek}
      style={
        {
          ...(sizing === 'rail' ? { flexShrink: 0 } : {}),
          '--peek-bg': TONE[tone].bg,
          '--peek-offset': TRACK_PEEK_OFFSET,
        } as CSSProperties
      }
    >
      <CardLink
        href={`/resources/learning-tracks/${track.id}`}
        ariaLabel={t('list.openTrackAria', { title: track.title })}
        linkAs={RouterLinkAs}
        pad="flush"
        direction="column"
        clip
        {...(sizing === 'rail' ? { style: { width: TRACK_CARD_WIDTH } } : {})}
      >
        <MediaHeader
          {...(cover !== null ? { src: cover } : { bg: trackFallbackBg(tone) })}
          tone={tone}
          height={TRACK_COVER_HEIGHT}
          scrim
          topLeft={
            showBadge ? (
              <span className={`${styles.chip} ${styles.chipLight}`}>
                <Icon size="sm">{layersIcon}</Icon>
                <span>{t('card.badge')}</span>
              </span>
            ) : undefined
          }
          topRight={
            !track.published ? (
              <Pill tone="neutral" size="sm">
                {t('card.draft')}
              </Pill>
            ) : undefined
          }
          bottomLeft={
            <Stack gap="sm" align="flex-start">
              {/* eslint-disable-next-line @leanin/ui/typography-component-required -- same semibold overlay-title exception as ResourceShelfCard */}
              <h3 className={styles.overlayTitle}>{track.title}</h3>
              <span className={`${styles.chip} ${styles.chipDark}`}>
                {t('card.resourceCount', { count: track.resourceCount })}
              </span>
            </Stack>
          }
        />
      </CardLink>
    </div>
  );
}
